/**
 * 可投入资金（investable_funds）选项工具
 * - 字典权威源：UDC（运行时传入 allDicts / 字典数组）
 * - 无 React / antd 依赖，PC 与移动端可共用
 *
 * 最新可选：UDC 中除废弃项外的全部 investable_funds
 * 历史兼容：仅写死废弃 code（编辑时可并入下拉，允许原值提交保存）
 */

export const INVESTABLE_FUNDS_TYPE = 'investable_funds';

/** 已废弃、仅历史兼容（不出现在新建下拉） */
export const INVESTABLE_FUNDS_LEGACY_CODES = ['1', '2'] as const;

export type InvestableFundsMode = 'create' | 'edit' | 'view';

export interface DictOptionLike {
  type?: string;
  code: string | number;
  label: string;
  sort?: number;
  desc?: string;
  [key: string]: any;
}

export function isInvestableFundsLegacyCode(code: string | number | null | undefined): boolean {
  if (code == null || code === '') return false;
  return (INVESTABLE_FUNDS_LEGACY_CODES as readonly string[]).includes(String(code));
}

/** 从全量字典中取出 investable_funds */
export function getInvestableFundsDict(allDicts: DictOptionLike[] = []): DictOptionLike[] {
  return (allDicts || []).filter((item) => item?.type === INVESTABLE_FUNDS_TYPE);
}

/** 最新码表：UDC 全量减去废弃项，按 sort 排序 */
export function getInvestableFundsLatestOptions(allDicts: DictOptionLike[] = []): DictOptionLike[] {
  return getInvestableFundsDict(allDicts)
    .filter((item) => !isInvestableFundsLegacyCode(item.code))
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
}

export function isInvestableFundsLatestCode(
  allDicts: DictOptionLike[] = [],
  code?: string | number | null,
): boolean {
  if (code == null || code === '') return false;
  if (isInvestableFundsLegacyCode(code)) return false;
  return getInvestableFundsLatestOptions(allDicts).some((item) => String(item.code) === String(code));
}

function normalizeCodes(value?: string | number | (string | number)[] | null): string[] {
  if (value == null || value === '') return [];
  const list = Array.isArray(value) ? value : [value];
  return list.map((item) => String(item)).filter((item) => item !== '');
}

/**
 * 获取可投入资金下拉选项（PC / 移动端共用）
 *
 * - create：仅最新码表（UDC − 废弃项）
 * - edit：当前值均在最新码表内 → 仅最新；含废弃项（如 1/2）→ 最新 ∪ 当前旧项（允许直接保存旧值）
 * - view：全量字典（新旧均可解析展示）
 *
 * value 支持单选或多选（策略规则）
 */
export function getInvestableFundsOptions(
  allDicts: DictOptionLike[] = [],
  mode: InvestableFundsMode = 'create',
  value?: string | number | (string | number)[] | null,
): DictOptionLike[] {
  const all = getInvestableFundsDict(allDicts);
  const latest = getInvestableFundsLatestOptions(allDicts);

  if (mode === 'view') {
    return all;
  }

  if (mode === 'create') {
    return latest;
  }

  // edit
  const currents = normalizeCodes(value);
  if (!currents.length) {
    return latest;
  }

  const extras = currents
    .filter((code) => !latest.some((item) => String(item.code) === code))
    .map((code) => all.find((item) => String(item.code) === code))
    .filter((item): item is DictOptionLike => Boolean(item));

  if (!extras.length) {
    return latest;
  }

  return [...latest, ...extras];
}

/** code → label（查看 / 列表），基于全量 UDC 字典 */
export function getInvestableFundsLabel(
  allDicts: DictOptionLike[] = [],
  code?: string | number | null,
): string {
  if (code == null || code === '') return '';
  const hit = getInvestableFundsDict(allDicts).find((item) => String(item.code) === String(code));
  return hit?.label ?? '';
}
